// CalendarThemeModal — pt89T (Feb 20, 2026, Randy).
//
// AI Re-Themer Stage B modal. Rewrites this silo's AI Calendar
// vocabulary to fit ANY theme — welding shop, dog training gym, sales
// team, kids soccer, whatever the owner is actually running. The map
// persists in `silo.theme_map` and is read on every render via the
// `useThemeLabel(...)` hook seeded by `<CalendarThemeProvider>`.
//
// Flow:
//   1. Owner supplies THEME (required) + optional members / tone /
//      avoid_words / rewrite-tooltips toggle.
//   2. Tap "Preview" → hits POST /calendar/silos/{id}/calendar-theme/generate
//      → LLM returns a scrubbed `theme_map` (server-side reserved-word
//      blocking + char-budget enforcement).
//   3. UI shows a "Before → After" diff. Owner can regenerate any row
//      or manually edit any row (pencil = plain text override).
//   4. Tap "Apply theme" → hits POST /calendar/silos/{id}/calendar-theme
//      → the whole subtree re-renders with the new labels.
//
// Reset button below the fold wipes the theme_map back to defaults.
// A "Last 5 revisions" dropdown shows recent maps for one-tap revert.

import { useEffect, useMemo, useState } from "react";
import { X, Palette, Sparkle, Spinner, ArrowClockwise, PencilSimple, ArrowUUpLeft, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { THEME_MANIFEST } from "@/lib/themeManifest";
import { useCalendarTheme } from "@/lib/CalendarThemeProvider";

const TONE_OPTIONS = [
  { v: "warm",             label: "Warm",             hint: "friendly, personal, coffee-shop energy" },
  { v: "professional",     label: "Professional",     hint: "trusted, credible, work-appropriate" },
  { v: "formal-corporate", label: "Formal corporate", hint: "buttoned-up, agency / enterprise" },
  { v: "terse",            label: "Terse",            hint: "short, punchy, no filler" },
  { v: "playful",          label: "Playful",          hint: "fun, energetic, on-brand for kids/hobby silos" },
];

export default function CalendarThemeModal({ siloId, open, onClose }) {
  const { reload } = useCalendarTheme();

  // Manifest = the exact keys we send to the backend. Frozen once per open.
  const manifestArr = useMemo(() => (
    Object.entries(THEME_MANIFEST).map(([key, e]) => ({
      key,
      default: e.defaultLabel,
      description: e.description,
      char_budget: e.charBudget || 40,
    }))
  ), []);

  // Owner-supplied inputs
  const [themeDescription, setThemeDescription] = useState("");
  const [membersDescription, setMembersDescription] = useState("");
  const [tone, setTone] = useState("warm");
  const [avoidInput, setAvoidInput] = useState("");
  const [avoidWords, setAvoidWords] = useState([]);
  const [alsoRewriteTooltips, setAlsoRewriteTooltips] = useState(true); // pt89U (Feb 20, 2026, Randy) — kept in state for backwards-shape but no longer surfaced in UI. Universal tooltip decision means tooltips are theme-agnostic; the toggle is dead.

  // Generation state
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null); // { theme_map, notes }
  const [manualOverrides, setManualOverrides] = useState({}); // key → string
  const [existing, setExisting] = useState({ theme_map: {}, theme_meta: {}, theme_history: [] });
  // Iter133 fix (Randy) — replace window.confirm() for the destructive
  // reset button with a two-tap in-modal state. Headless Playwright
  // auto-dismisses window.confirm, so the browser-side test suite could
  // never actually exercise the DELETE path. Two-tap "Confirm reset?"
  // is also gentler UX than a browser alert.
  const [resetArmed, setResetArmed] = useState(false);

  // Load existing theme + history when the modal opens
  useEffect(() => {
    if (!open || !siloId) return;
    (async () => {
      try {
        const r = await api.get(`/calendar/silos/${siloId}/calendar-theme`);
        setExisting({
          theme_map: r.data?.theme_map || {},
          theme_meta: r.data?.theme_meta || {},
          theme_history: r.data?.theme_history || [],
        });
      } catch { /* empty is fine */ }
    })();
  }, [open, siloId]);

  // Merged preview must be declared before the early-return so the hook
  // order stays stable across open/closed states.
  const mergedPreview = useMemo(() => {
    // Manual overrides shadow the LLM's suggestion, which shadows the default.
    const map = { ...(preview?.theme_map || {}) };
    for (const [k, v] of Object.entries(manualOverrides)) {
      if (v && v.trim()) map[k] = v.trim();
      else delete map[k];
    }
    return map;
  }, [preview, manualOverrides]);

  // Jun 2026 (Randy) — vocabulary top-up. If the manifest grew new keys
  // after this theme was generated, those slots show default family words.
  // Detect the gap and offer a one-tap non-destructive fill.
  const [toppingUp, setToppingUp] = useState(false);
  const missingKeys = useMemo(() => {
    const tm = existing.theme_map || {};
    if (!Object.keys(tm).length) return [];
    return manifestArr.filter((m) => !(m.key in tm)).map((m) => m.key);
  }, [existing.theme_map, manifestArr]);

  if (!open) return null;

  const topup = async () => {
    setToppingUp(true);
    try {
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/topup`, { manifest: manifestArr });
      setExisting((cur) => ({ ...cur, theme_map: r.data?.theme_map || cur.theme_map }));
      toast.success(`Filled ${r.data?.count ?? 0} missing words — the whole silo now speaks your theme.`);
      await reload();
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") {
        toast.error("Add your BYOK key or top up your voucher to run the AI.");
        return;
      }
      toast.error(e.response?.data?.detail || "Couldn't fill the missing words this round.");
    } finally { setToppingUp(false); }
  };

  const addAvoidChip = (raw) => {
    const v = (raw || "").trim().replace(/,+$/, "");
    if (!v) return;
    if (avoidWords.includes(v)) { setAvoidInput(""); return; }
    setAvoidWords((cur) => [...cur, v]);
    setAvoidInput("");
  };
  const removeAvoidChip = (chip) => setAvoidWords((cur) => cur.filter((c) => c !== chip));

  const generate = async () => {
    if (!themeDescription.trim()) { toast.error("Describe the theme in a sentence first."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/generate`, {
        theme_description: themeDescription.trim(),
        members_description: membersDescription.trim() || null,
        tone,
        avoid_words: avoidWords,
        // pt89U — always false. Tooltips are theme-agnostic; the flag is
        // preserved on the backend for shape compat but has no effect.
        also_rewrite_tooltips: false,
        manifest: manifestArr,
      });
      setPreview({
        theme_map: r.data?.theme_map || {},
        // Feb 26, 2026 (Randy pt-kid-propose-themed-starters) — carry
        // the LLM's `starters` array through Preview → Apply so it
        // lands on the silo doc's theme_meta.starters at apply time.
        starters: Array.isArray(r.data?.starters) ? r.data.starters : [],
        notes: r.data?.notes || "",
      });
      setManualOverrides({});
      if (!Object.keys(r.data?.theme_map || {}).length) {
        toast.warning("The Re-Themer declined this theme — try refining the description.");
      } else {
        toast.success(`Previewed ${Object.keys(r.data.theme_map).length} labels — review and Apply.`);
      }
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") {
        toast.error("Add your BYOK key or top up your voucher to run the AI.");
        return;
      }
      toast.error(e.response?.data?.detail || "Couldn't generate a theme this round.");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (Object.keys(mergedPreview).length === 0) { toast.error("Nothing to apply — Preview first."); return; }
    if (!window.confirm(
      "Apply this theme?\n\n" +
      "This silo's current calendar, chores, suggestions and rewards get packed away into the CURRENT theme's memory, and the new theme starts fresh — or is restored if you've used it here before. " +
      "Kid avatar emojis reset to \u2b50. Nothing is permanently deleted: switch back any time and it all comes back. People and your silo memory are never touched."
    )) return;
    setApplying(true);
    try {
      await api.post(`/calendar/silos/${siloId}/calendar-theme`, {
        theme_map: mergedPreview,
        tone,
        source_prompt: themeDescription.trim(),
        avoid_words: avoidWords,
        starters: preview?.starters || [],
      });
      toast.success("Theme applied. Every word on this silo is now yours.");
      await reload();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't apply the theme.");
    } finally { setApplying(false); }
  };

  const resetTheme = async () => {
    // Iter133 fix — two-tap arming. First tap arms, second tap fires
    // DELETE. Auto-disarms after 4s so accidental first-taps don't
    // sit forever waiting for a confirmation.
    if (!resetArmed) {
      setResetArmed(true);
      setTimeout(() => setResetArmed(false), 4000);
      return;
    }
    setResetArmed(false);
    try {
      await api.delete(`/calendar/silos/${siloId}/calendar-theme`);
      toast.success("Theme reset to defaults.");
      await reload();
      onClose && onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't reset."); }
  };

  const revertToHistoryRow = async (row) => {
    if (!row?.theme_map || !window.confirm(
      "Revert to this earlier theme?\n\n" +
      "The current theme's calendar, chores, suggestions and rewards pack away into its memory, and this theme's own content is restored (or starts fresh). " +
      "Kid avatar emojis reset to \u2b50. Nothing is permanently deleted. People and your silo memory are never touched."
    )) return;
    try {
      await api.post(`/calendar/silos/${siloId}/calendar-theme`, {
        theme_map: row.theme_map,
        tone: row.theme_meta?.tone || null,
        source_prompt: row.theme_meta?.source_prompt || null,
        avoid_words: row.theme_meta?.avoid_words || [],
      });
      toast.success("Reverted.");
      await reload();
      onClose && onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Revert failed."); }
  };

  // pt89W (Feb 20, 2026, Randy) — Named archive + JSON export. Owners can
  // pin a memorable name onto the active theme OR onto any archived entry,
  // then export the whole thing as a portable JSON file that can be
  // hand-imported into another silo they own.
  const nameCurrent = async () => {
    const current = window.prompt("Name this theme (e.g. 'welding shop', 'book club'):", existing.theme_meta?.name || "");
    if (current === null) return; // user cancelled
    try {
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/current/name`, { name: current });
      setExisting((cur) => ({ ...cur, theme_meta: r.data?.theme_meta || cur.theme_meta }));
      toast.success(current ? `Named "${current}".` : "Name cleared.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the name."); }
  };
  const nameArchived = async (row) => {
    const name = window.prompt("Name this archived theme:", row.name || "");
    if (name === null) return;
    try {
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/history/${row.id}/name`, { name });
      setExisting((cur) => ({ ...cur, theme_history: r.data?.theme_history || cur.theme_history }));
      toast.success(name ? `Named "${name}".` : "Name cleared.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the name."); }
  };
  const exportTheme = async () => {
    try {
      const r = await api.get(`/calendar/silos/${siloId}/calendar-theme/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      const safe = (r.data?.theme_meta?.name || r.data?.silo_name_at_export || "silo-theme").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
      link.href = URL.createObjectURL(blob);
      link.download = `mysilo-theme-${safe}-${stamp}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Theme downloaded.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't export the theme."); }
  };

  return (
    <div className="fixed inset-0 z-[260] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose} data-testid="calendar-theme-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFCF8] rounded-2xl w-full max-w-2xl max-h-[92dvh] flex flex-col shadow-2xl border hairline">

        <div className="px-5 py-3 border-b hairline flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Palette size={20} weight="duotone" className="text-[#C19A4A]" />
            <h2 className="font-serif-display text-xl text-[#1C1B1A]">Re-theme this Silo</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]"
            data-testid="calendar-theme-close" aria-label="Close"><X size={17} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Jun 2026 (Randy) — top-up banner. Shows only when the ACTIVE
              theme predates newer manifest keys (those slots currently fall
              back to default family words). One tap fills just the gaps —
              no wipe, no content change. */}
          {missingKeys.length > 0 && (
            <div data-testid="theme-topup-banner"
              className="flex items-center gap-3 flex-wrap bg-[#F8F1E2] border border-[#EAD9AE] rounded-md px-3 py-2.5">
              <div className="flex-1 min-w-[220px] text-[11px] text-[#6B4E1E] leading-relaxed">
                <strong>{missingKeys.length} newer labels</strong> were added after this theme was made — those spots are showing the default family words right now.
              </div>
              <button onClick={topup} disabled={toppingUp || busy || applying}
                data-testid="theme-topup-btn"
                className="text-xs px-3 py-2 rounded-md bg-[#C19A4A] text-white hover:bg-[#A9853C] disabled:opacity-50 flex items-center gap-1.5">
                {toppingUp ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="duotone" />}
                {toppingUp ? "Filling…" : `Fill in ${missingKeys.length} missing words`}
              </button>
            </div>
          )}

          {/* Inputs */}
          <section className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">What is this space?</span>
              <textarea value={themeDescription} onChange={(e) => setThemeDescription(e.target.value)}
                data-testid="theme-description"
                placeholder="e.g. A welding shop where the owner trains 3 apprentices — daily safety checks, weekly stock, monthly customer meetings."
                rows={3} maxLength={600}
                className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm focus:outline-none focus:border-[#4A5D4E] leading-relaxed" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Who are the members? <span className="normal-case text-[#8A8A88] italic">(optional)</span></span>
              <input value={membersDescription} onChange={(e) => setMembersDescription(e.target.value)}
                data-testid="theme-members"
                placeholder="e.g. Owner + 3 apprentices"
                maxLength={200}
                className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm focus:outline-none focus:border-[#4A5D4E]" />
            </label>
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] block mb-1.5">Tone</span>
              <div className="flex flex-wrap gap-1.5">
                {TONE_OPTIONS.map((o) => (
                  <button key={o.v} onClick={() => setTone(o.v)}
                    data-testid={`theme-tone-${o.v}`}
                    title={o.hint}
                    className={`text-[11px] px-2.5 py-1.5 rounded-md border transition-colors ${tone === o.v ? "bg-[#1C1B1A] text-white border-[#1C1B1A]" : "bg-white hairline hover:border-[#4A5D4E] text-[#3A3A38]"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Words to avoid <span className="normal-case text-[#8A8A88] italic">(protects against unwanted vocabulary)</span></span>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 px-2 py-1.5 border hairline rounded-md bg-white min-h-[42px]">
                {avoidWords.map((w) => (
                  <span key={w} className="text-[11px] bg-[#FBEBE4] text-[#8A2E1F] rounded-full px-2 py-0.5 flex items-center gap-1"
                    data-testid={`theme-avoid-chip-${w}`}>
                    {w}
                    <button onClick={() => removeAvoidChip(w)} className="text-[#8A2E1F] hover:text-[#C16E5A]"
                      data-testid={`theme-avoid-remove-${w}`} aria-label="Remove">
                      <X size={11} weight="bold" />
                    </button>
                  </span>
                ))}
                <input value={avoidInput} onChange={(e) => setAvoidInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "," || e.key === "Tab") { e.preventDefault(); addAvoidChip(avoidInput); }
                    if (e.key === "Backspace" && !avoidInput && avoidWords.length > 0) {
                      setAvoidWords((cur) => cur.slice(0, -1));
                    }
                  }}
                  onBlur={() => addAvoidChip(avoidInput)}
                  data-testid="theme-avoid-input"
                  placeholder="type + Enter or comma"
                  className="flex-1 min-w-[120px] px-1 py-0.5 text-sm bg-transparent focus:outline-none" />
              </div>
            </label>
            {/* pt89U (Feb 20, 2026, Randy) — the "Also rewrite tooltips"
                toggle was removed. Decision: tooltips are theme-agnostic
                explanations of what a button does, using generic
                household/member/task language. They never get re-themed.
                This drops ~40 keys out of the Re-Themer manifest scope
                and prevents jargon leakage. See PRD note pt89U. */}
            <div className="text-[10px] text-[#6B6A68] italic bg-white border hairline rounded-md px-3 py-2">
              <strong>Tooltips are theme-agnostic.</strong> Button explanations use neutral language (&ldquo;household&rdquo;, &ldquo;member&rdquo;, &ldquo;task&rdquo;) that reads fine under any theme, so they&rsquo;re never rewritten by the Re-Themer.
            </div>
          </section>

          <div className="flex items-center gap-2">
            <button onClick={generate} disabled={busy || applying}
              data-testid="theme-generate-btn"
              className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
              {busy ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="duotone" />} Preview theme
            </button>
            {preview && (
              <span className="text-[11px] text-[#6B6A68] italic" data-testid="theme-preview-count">
                {Object.keys(mergedPreview).length} labels ready
              </span>
            )}
          </div>

          {/* Preview diff */}
          {preview && (
            <section data-testid="theme-preview-section">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Before → After</div>
              <div className="text-[10px] text-[#4A5D4E] bg-[#F4F8F5] border border-[#D8E4DA] rounded-md px-3 py-2 mb-2" data-testid="theme-keep-default-hint">
                Type anything you like into a box — <strong>including the original word</strong>. A <span className="font-semibold text-[#4A9E6A]">✓</span> means the word matches the default; it still applies exactly as typed.
              </div>
              {preview.notes && (
                <div className="text-[11px] text-[#6B6A68] italic bg-[#F8F1E2] border border-[#EAD9AE] rounded-md px-3 py-2 mb-2">
                  {preview.notes}
                </div>
              )}
              <div className="space-y-1">
                {manifestArr.map((m) => {
                  const suggested = preview.theme_map[m.key];
                  const override = manualOverrides[m.key];
                  const final = (override != null ? override : suggested) || m.default;
                  const unchanged = final === m.default;
                  return (
                    <div key={m.key} className={`grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 px-2.5 py-1.5 rounded border ${unchanged ? "bg-white border-[#D8E4DA]" : "bg-[#F4F8F5] border-[#B9D4BE]"}`}
                      data-testid={`theme-row-${m.key}`}
                      title={unchanged ? "Same as the default — keeping the same word is perfectly fine and it WILL be applied." : (m.description || "")}>
                      <div className={`text-xs text-[#6B6A68] truncate ${unchanged ? "" : "line-through"}`} title={m.description || ""}>{m.default}</div>
                      <div className={unchanged ? "text-[#4A9E6A] text-xs font-semibold" : "text-[#8A8A88]"} title={unchanged ? "Kept as-is — still applies" : "Replaced"}>{unchanged ? "✓" : "→"}</div>
                      <input value={final}
                        onChange={(e) => setManualOverrides((cur) => ({ ...cur, [m.key]: e.target.value }))}
                        data-testid={`theme-input-${m.key}`}
                        maxLength={m.char_budget}
                        className="text-xs px-2 py-1 border hairline rounded bg-white truncate focus:outline-none focus:border-[#4A5D4E] text-[#1C1B1A]" />
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button onClick={() => setManualOverrides((cur) => { const n = { ...cur }; delete n[m.key]; return n; })}
                          data-testid={`theme-clear-override-${m.key}`}
                          title="Clear my override on this row"
                          className="p-1 rounded text-[#B5B3AE] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]">
                          <ArrowUUpLeft size={11} weight="bold" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* History */}
          {existing.theme_history?.length > 0 && (
            <section className="pt-3 border-t hairline">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex-1">Saved themes</div>
                {existing.theme_map && Object.keys(existing.theme_map).length > 0 && (
                  <>
                    <button onClick={nameCurrent} data-testid="theme-name-current"
                      title="Give the current active theme a memorable name"
                      className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1 text-[#4A5D4E]">
                      <PencilSimple size={11} weight="duotone" /> Name current
                    </button>
                    <button onClick={exportTheme} data-testid="theme-export-btn"
                      title="Download this silo's theme as a portable JSON file"
                      className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1 text-[#4A5D4E]">
                      <ArrowUUpLeft size={11} weight="bold" style={{ transform: "rotate(-90deg)" }} /> Download JSON
                    </button>
                  </>
                )}
              </div>
              {existing.theme_meta?.name && (
                <div className="text-[11px] italic text-[#4A5D4E] bg-[#F4F8F5] border border-[#B9D4BE] rounded px-2.5 py-1.5 mb-2" data-testid="theme-current-name">
                  Active theme: <strong>{existing.theme_meta.name}</strong>
                </div>
              )}
              <div className="space-y-1">
                {existing.theme_history.map((row) => (
                  <div key={row.id} className="flex items-center gap-2 text-xs bg-white border hairline rounded px-2.5 py-1.5">
                    <span className="text-[#6B6A68] flex-1 truncate">
                      {row.name ? <strong className="text-[#1C1B1A]">{row.name}</strong> : (row.theme_meta?.source_prompt?.slice(0, 60) || "Untitled theme")}
                      {row.theme_meta?.tone && <span className="ml-2 text-[9px] uppercase tracking-[0.14em] text-[#8A8A88]">· {row.theme_meta.tone}</span>}
                    </span>
                    <button onClick={() => nameArchived(row)}
                      data-testid={`theme-name-archived-${row.id}`}
                      title="Name this archived theme"
                      className="text-[10px] px-1.5 py-1 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]">
                      <PencilSimple size={10} weight="duotone" />
                    </button>
                    <button onClick={() => revertToHistoryRow(row)}
                      data-testid={`theme-revert-${row.id}`}
                      title="Revert this silo to this earlier theme"
                      className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1">
                      <ArrowClockwise size={11} weight="bold" /> Revert
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t hairline flex items-center gap-2">
          {existing.theme_map && Object.keys(existing.theme_map).length > 0 && (
            <button onClick={resetTheme} data-testid="theme-reset-btn"
              title="Reset this silo's labels back to My Silo defaults (two taps to confirm)"
              className={`text-xs px-3 py-2 rounded flex items-center gap-1.5 transition-colors ${resetArmed ? "bg-[#C16E5A] text-white hover:bg-[#a75747]" : "text-[#C16E5A] hover:bg-[#FBEBE4]"}`}>
              <Trash size={12} weight="duotone" />
              {resetArmed ? "Tap again to confirm reset" : "Reset to defaults"}
            </button>
          )}
          <span className="flex-1" />
          <button onClick={onClose} data-testid="theme-cancel"
            className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE]">Cancel</button>
          <button onClick={apply}
            disabled={applying || Object.keys(mergedPreview).length === 0}
            data-testid="theme-apply-btn"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {applying ? <Spinner size={12} className="animate-spin" /> : <PencilSimple size={12} weight="duotone" />}
            Apply theme
          </button>
        </div>
      </div>
    </div>
  );
}
